import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { LogOut, Loader2, Menu, X } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import ContentView from "@/components/dataroom/ContentView";
import GlobalSearch from "@/components/dataroom/GlobalSearch";
import { LOGO_URL } from "@/lib/nvsnContent";
import { ThemeToggle, useThemeClass } from "@/components/ThemeToggle";

const GROUP_ORDER = ["Overview", "Pillars", "Diligence"];

export default function DataRoom() {
  const navigate = useNavigate();
  const { investor, logoutInvestor } = useAuth();
  const [sections, setSections] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [menuOpen, setMenuOpen] = useState(false);
  const [theme, setTheme] = useThemeClass();

  const load = async () => {
    try {
      const { data } = await api.get("/sections");
      setSections(data);
      setActiveId((cur) => cur || data[0]?.id || null);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);
  useEffect(() => { if (activeId) api.post(`/sections/${activeId}/view`).catch(() => {}); }, [activeId]);

  const active = sections.find((s) => s.id === activeId);
  const logout = () => { logoutInvestor(); navigate("/"); };
  const goToSlug = (slug) => { const s = sections.find((x) => x.slug === slug); if (s) select(s.id); };
  const select = (id) => { setActiveId(id); setMenuOpen(false); };

  const grouped = GROUP_ORDER.map((g) => ({ group: g, items: sections.filter((s) => s.group === g) })).filter((x) => x.items.length);

  const NavList = () => (
    <>
      {grouped.map((grp) => (
        <div key={grp.group} className="mb-6">
          <div className="font-mono-dm uppercase tracking-[0.12em] text-[9px] text-[#8f8f8f] px-6 mb-2">{grp.group}</div>
          {grp.items.map((s) => (
            <button
              key={s.id}
              data-testid={`nav-section-${s.id}`}
              onClick={() => select(s.id)}
              className={`w-full flex items-center gap-2.5 pl-6 pr-4 py-2.5 text-[12px] border-l-2 transition-colors ${
                activeId === s.id ? "text-[#4deb7a] border-[#4deb7a] bg-[rgba(77,235,122,0.08)] font-medium" : "text-[#d4d4d4] border-transparent hover:text-[#f0f0f0] hover:bg-[#141414]"
              }`}
            >
              <span className={`w-[5px] h-[5px] rounded-full shrink-0 ${activeId === s.id ? "bg-[#4deb7a]" : "bg-[#3a3a3a]"}`} />
              <span className="truncate text-left flex-1">{s.title}</span>
              <span className={`font-mono-dm text-[9px] px-1.5 py-0.5 rounded-full min-w-[18px] text-center ${activeId === s.id ? "bg-[rgba(77,235,122,0.08)] text-[#4deb7a] border border-[rgba(77,235,122,0.2)]" : "bg-[#1a1a1a] text-[#8f8f8f]"}`}>
                {s.file_count + s.link_count}
              </span>
            </button>
          ))}
        </div>
      ))}
    </>
  );

  return (
    <div className="dr-scale min-h-screen flex flex-col bg-[#080808] text-[#e8e8e8]">
      {/* Header */}
      <header className="h-[60px] border-b border-[#222] px-5 sm:px-10 flex items-center justify-between sticky top-0 bg-[#080808] z-40">
        <div className="flex items-center gap-3">
          <button data-testid="menu-toggle" onClick={() => setMenuOpen(true)} className="md:hidden text-[#d4d4d4] hover:text-[#f0f0f0] transition-colors" aria-label="Open menu">
            <Menu className="h-6 w-6" strokeWidth={1.5} />
          </button>
          <div className="hidden md:flex items-center gap-5">
            <img src={LOGO_URL} alt="NVSN" className="h-9 w-auto" />
            <span className="w-px h-[18px] bg-[#2a2a2a]" />
            <span className="mono-eyebrow text-[#bdbdbd]">Investor Data Room · Pre-Seed 2026</span>
          </div>
        </div>
        <div className="flex-1 max-w-[420px] mx-3 sm:mx-6">
          <GlobalSearch sections={sections} onSelect={select} />
        </div>
        <div className="flex items-center gap-4">
          <img src={LOGO_URL} alt="NVSN" className="md:hidden h-8 w-auto" />
          <span className="hidden md:inline mono-eyebrow text-[#bdbdbd] border border-[#222] px-2.5 py-1 rounded-sm">Confidential</span>
          <button data-testid="logout-btn" onClick={logout} className="hidden md:flex items-center gap-1.5 text-[12px] text-[#d4d4d4] hover:text-[#f0f0f0] transition-colors">
            <LogOut className="h-3.5 w-3.5" strokeWidth={1.5} /> Sign out
          </button>
        </div>
      </header>

      <div className="flex flex-1">
        {/* Desktop Sidebar */}
        <nav className="w-[244px] min-w-[244px] border-r border-[#222] pt-8 sticky top-[60px] h-[calc(100vh-60px)] overflow-y-auto hidden md:flex flex-col">
          <NavList />
          <div className="px-6 pt-4 mt-2">
            <div className="text-[12px] text-[#f0f0f0] truncate">{investor?.first_name} {investor?.last_name}</div>
            <div className="text-[11px] text-[#8f8f8f] truncate">{investor?.company}</div>
          </div>
          <div className="mt-auto px-5 py-5 border-t border-[#1a1a1a]">
            <ThemeToggle theme={theme} setTheme={setTheme} />
          </div>
        </nav>

        {/* Mobile Drawer */}
        <AnimatePresence>
          {menuOpen && (
            <div className="md:hidden fixed inset-0 z-50">
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setMenuOpen(false)} className="absolute inset-0 bg-black/70" />
              <motion.nav
                initial={{ x: "-100%" }} animate={{ x: 0 }} exit={{ x: "-100%" }} transition={{ type: "tween", duration: 0.25 }}
                className="absolute left-0 top-0 h-full w-[82%] max-w-[300px] bg-[#0b0b0b] border-r border-[#222] flex flex-col"
                data-testid="mobile-drawer"
              >
                <div className="h-[60px] px-6 flex items-center justify-between border-b border-[#222] shrink-0">
                  <img src={LOGO_URL} alt="NVSN" className="h-8 w-auto" />
                  <button data-testid="menu-close" onClick={() => setMenuOpen(false)} className="text-[#d4d4d4] hover:text-[#f0f0f0]"><X className="h-5 w-5" /></button>
                </div>
                <div className="flex-1 overflow-y-auto py-6">
                  <NavList />
                </div>
                <div className="border-t border-[#222] p-4 shrink-0">
                  <div className="mb-3"><ThemeToggle theme={theme} setTheme={setTheme} testid="theme-toggle-mobile" /></div>
                  <div className="px-2 mb-3">
                    <div className="text-[12px] text-[#f0f0f0] truncate">{investor?.first_name} {investor?.last_name}</div>
                    <div className="text-[11px] text-[#8f8f8f] truncate">{investor?.company}</div>
                  </div>
                  <button data-testid="logout-btn-mobile" onClick={logout} className="w-full flex items-center gap-2 text-[13px] text-[#d4d4d4] hover:text-[#f0f0f0] px-2 py-2.5 border border-[#222] rounded transition-colors">
                    <LogOut className="h-4 w-4" strokeWidth={1.5} /> Sign out
                  </button>
                </div>
              </motion.nav>
            </div>
          )}
        </AnimatePresence>

        {/* Main */}
        <main className="flex-1 min-w-0 w-full px-5 sm:px-10 lg:px-14 py-8 sm:py-12">
          {loading ? (
            <div className="flex items-center gap-2 text-[#bdbdbd] py-20 text-[13px]"><Loader2 className="h-4 w-4 animate-spin" /> Loading data room…</div>
          ) : active ? (
            <motion.div key={active.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
              <ContentView section={active} isAdmin={false} onNavigateSlug={goToSlug} />
            </motion.div>
          ) : (
            <div className="py-24 text-center text-[#bdbdbd]">
              <div className="text-[28px] opacity-30 mb-3">◻</div>
              <p className="font-serif-pf text-[24px] text-[#f0f0f0]">The data room is being prepared</p>
              <p className="text-[12px] mt-2">Materials will be published shortly.</p>
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
